import React from "react";
import styled from "styled-components";

const Page = styled.div`
  --bg: #0a0a12;
  --panel: #101018;
  --surface: #16161f;
  --text: #f5f6fa;
  --dim: #a2a6b4;
  --faint: #6c7080;
  --accent: #8785f8;
  --border: rgba(255, 255, 255, 0.08);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
  }
`;

const Wrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 22px 80px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 22px;
  margin-bottom: 28px;
  border-bottom: 1px solid var(--border);
`;

const Badge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: linear-gradient(150deg, #5b5fe9, #7c6bf2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const Brand = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.2px;

  span {
    color: var(--accent);
  }
`;

const Title = styled.h1`
  font-size: 30px;
  margin: 6px 0 4px;
`;

const Updated = styled.p`
  color: var(--faint);
  font-size: 14px;
  margin-bottom: 28px;
`;

const Section = styled.h2`
  font-size: 19px;
  margin: 34px 0 10px;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--accent);
    border-radius: 2px;
    flex: none;
  }
`;

const Body = styled.div`
  p,
  li {
    color: var(--dim);
    font-size: 16px;
  }

  ul {
    padding-left: 20px;
  }

  li {
    margin-bottom: 10px;
  }

  strong {
    color: var(--text);
    font-weight: 600;
  }

  a {
    color: var(--accent);
  }
`;

const Card = styled.div`
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px 18px;
  color: var(--dim);
  font-size: 15px;
`;

const Note = styled.div`
  background: var(--surface);
  border-left: 3px solid var(--accent);
  border-radius: 8px;
  padding: 14px 16px;
  margin: 16px 0;
  color: var(--dim);
  font-size: 15px;
`;

const Footer = styled.footer`
  margin-top: 48px;
  padding-top: 22px;
  border-top: 1px solid var(--border);
  color: var(--faint);
  font-size: 14px;

  a {
    color: var(--accent);
  }
`;

const Mark = () => (
  <svg viewBox="0 0 512 512" role="img" aria-label="AI Utility Hub">
    <g
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="34"
      strokeLinecap="butt"
      strokeLinejoin="miter"
      strokeMiterlimit="1.2"
    >
      <path d="M78 344 L128 184 L178 344" />
      <path d="M99 276 L157 276" />
      <path d="M222 184 L222 292 A42 42 0 0 0 306 292 L306 184" />
      <path d="M352 184 L352 344" />
      <path d="M430 184 L430 344" />
      <path d="M352 276 L430 276" />
    </g>
  </svg>
);

const AiUtilityHubTerms = () => {
  return (
    <Page>
      <Wrap>
        <Header>
          <Badge>
            <Mark />
          </Badge>
          <Brand>
            AI Utility <span>Hub</span>
          </Brand>
        </Header>

        <Title>Terms &amp; Conditions</Title>
        <Updated>Last updated: 8 September 2026</Updated>

        <Body>
          <p>
            These terms govern your use of the AI Utility Hub mobile application
            (“the app”), published by Nimions (“we”, “us”). By installing or using
            the app you agree to them. If you do not agree, please do not use the
            app.
          </p>

          <Section as="h2">The service</Section>
          <p>
            AI Utility Hub provides a set of AI-powered writing tools — among them
            drafting, rewriting, summarising, translating, and generating emails,
            résumés, captions and code explanations. Tools may be added, changed
            or withdrawn over time.
          </p>

          <Section as="h2">Your account</Section>
          <p>
            The app creates an anonymous account automatically so you can start
            using it right away. You may optionally sign in with an email address,
            Google or Apple to keep your history across devices. You are
            responsible for activity that takes place under your account, and for
            keeping your sign-in credentials secure. You must be at least 13 years
            old to use the app.
          </p>

          <Section as="h2">Credits and free allowance</Section>
          <ul>
            <li>
              Generations are paid for with <strong>credits</strong>. Each tool
              shows its cost before you run it.
            </li>
            <li>
              New users receive a one-time grant of <strong>20 free credits</strong>
              . This grant is issued <strong>once per device</strong> — reinstalling
              the app or creating another account does not issue it again.
            </li>
            <li>
              You may be able to earn additional credits by choosing to watch a
              rewarded advertisement.
            </li>
            <li>
              Credits have <strong>no monetary value</strong>, cannot be exchanged
              for cash, and are not transferable between accounts.
            </li>
            <li>
              If a generation fails through a fault on our side, the credits for it
              are returned to your balance.
            </li>
          </ul>

          <Section as="h2">Pro subscription</Section>
          <ul>
            <li>
              Pro removes credit costs and advertising, and is available as a
              monthly or yearly subscription.
            </li>
            <li>
              Subscriptions are sold and billed through{" "}
              <strong>Google Play</strong>. Current pricing is the price shown in
              Google Play at the time of purchase.
            </li>
            <li>
              Subscriptions <strong>renew automatically</strong> until cancelled.
              You can cancel or manage a subscription at any time in the Google
              Play Store under Subscriptions; cancelling stops the next renewal and
              your Pro access continues until the end of the period you have
              already paid for.
            </li>
            <li>
              Refunds are handled by Google under the{" "}
              <strong>Google Play refund policy</strong>. We can ask Google to look
              at a case, but we cannot issue Play refunds ourselves.
            </li>
          </ul>

          <Section as="h2">AI-generated content</Section>
          <Note>
            AI output can be <strong>inaccurate, incomplete, biased or
            out of date</strong>, and similar prompts can produce similar results
            for different users. Always review and edit what the app produces
            before you rely on it, send it, publish it or submit it. Nothing the
            app generates is legal, medical, financial or professional advice.
          </Note>
          <ul>
            <li>
              <strong>Your input stays yours.</strong> We claim no ownership of the
              text you enter.
            </li>
            <li>
              <strong>Your output is yours to use.</strong> As between you and us,
              you may use the content the app generates for you, including
              commercially, subject to these terms and to the terms of the
              underlying AI providers.
            </li>
            <li>
              We cannot guarantee that generated content is original or that using
              it will not infringe someone else’s rights. Checking that is your
              responsibility.
            </li>
            <li>
              Your input is processed by third-party AI providers as described in
              our <a href="/privacy/ai-utility-hub">Privacy Policy</a>. Do not
              enter passwords, payment details, government identifiers, health
              records or other people’s confidential information.
            </li>
          </ul>

          <Section as="h2">Acceptable use</Section>
          <p>You agree not to use the app to:</p>
          <ul>
            <li>
              create content that is unlawful, hateful, harassing, defamatory,
              sexually explicit involving minors, or that incites violence;
            </li>
            <li>
              impersonate a person or organisation, or generate content designed to
              deceive, defraud or spam;
            </li>
            <li>
              generate malware, or material intended to breach security or privacy;
            </li>
            <li>
              infringe intellectual property or misuse confidential information;
            </li>
            <li>
              interfere with, reverse engineer, or attempt to bypass the app’s
              credit system, entitlement checks or rate limits, or use it through
              automated means;
            </li>
            <li>
              resell, sublicense or repackage the service as a competing product.
            </li>
          </ul>
          <p>
            We may suspend or terminate access to accounts that breach these
            rules.
          </p>

          <Section as="h2">Advertising</Section>
          <p>
            The free tier is supported by advertising served by Google AdMob,
            including banner ads and optional rewarded ads. Advertisers are
            independent of us and we are not responsible for the products or
            services they promote. Pro removes advertising.
          </p>

          <Section as="h2">Intellectual property</Section>
          <p>
            The app itself — its software, design, branding and the AI Utility Hub
            and Nimions names and logos — belongs to us and is protected by
            intellectual property law. These terms grant you a personal,
            non-exclusive, non-transferable, revocable licence to use the app; they
            transfer no ownership.
          </p>

          <Section as="h2">Availability</Section>
          <p>
            We aim to keep the app available but do not guarantee uninterrupted
            service. We may change, suspend or discontinue any part of it,
            including individual tools, and may need to do so for maintenance or
            for reasons outside our control. Generation depends on third-party AI
            providers, whose availability we do not control.
          </p>

          <Section as="h2">Termination</Section>
          <p>
            You may stop using the app at any time and remove your data as
            described in the Privacy Policy. We may suspend or terminate your
            access if you breach these terms or if we are required to by law.
            Unused credits are forfeited on termination for breach.
          </p>

          <Section as="h2">Disclaimer &amp; limitation of liability</Section>
          <p>
            The app is provided <strong>“as is” and “as available”</strong>,
            without warranties of any kind, whether express or implied, including
            fitness for a particular purpose and non-infringement. To the fullest
            extent permitted by law, we are not liable for indirect, incidental or
            consequential loss, nor for lost profits, lost data or reliance on
            AI-generated content. Where liability cannot be excluded, our total
            liability is limited to the amount you paid us in the{" "}
            <strong>twelve months</strong> before the claim arose. Nothing here
            limits liability that cannot lawfully be limited, and it does not
            affect consumer rights you have under the law of your country.
          </p>

          <Section as="h2">Changes to these terms</Section>
          <p>
            We may update these terms from time to time. Material changes will be
            reflected by the “Last updated” date above, and continuing to use the
            app after that date means you accept the updated terms.
          </p>

          <Section as="h2">Governing law</Section>
          <p>
            These terms are governed by the laws of{" "}
            <strong>Bangladesh</strong>, and the courts of Bangladesh have
            jurisdiction over any dispute, without affecting any mandatory
            consumer-protection rights available to you where you live.
          </p>

          <Section as="h2">Contact us</Section>
          <Card>
            Questions about these terms:
            <br />
            <a href="mailto:hello@nimions.com">hello@nimions.com</a>
            <br />
            <a href="tel:+8801764928732">+880 1764 928732</a>
          </Card>
        </Body>

        <Footer>
          © 2026 Nimions. These terms apply to the AI Utility Hub mobile
          application (package <strong>com.nimions.utilityhub</strong>). See also
          our <a href="/privacy/ai-utility-hub">Privacy Policy</a>.
        </Footer>
      </Wrap>
    </Page>
  );
};

export default AiUtilityHubTerms;
